import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { BookOpen, Calendar, User, ArrowRight, X, ChevronRight, Share2, Bookmark } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  author: string;
  date: string;
  category: string;
  slug?: string;
  published?: boolean;
}

import { useBrand } from '../context/BrandContext';
import { slugify, updateArticleSEO } from '../lib/seoUtils';
import { ArticleShare } from './ArticleShare';

export default function Blog() {
  const { resolveMediaUrl, stories: brandStories } = useBrand();

  const posts = (brandStories || [])
    .filter(s => s.published !== false) as unknown as BlogPost[];

  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const queryId = searchParams.get('id');

  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  // Find article from URL
  const activeArticle = posts.find(post =>
    (post.slug && post.slug === slug) ||
    slugify(post.title) === slug ||
    post.id === slug
  );

  // Backward compatibility:
  // /the-log?id=123 → /the-log/article-slug
  useEffect(() => {
    if (queryId && posts.length > 0) {
      const match = posts.find(post => post.id === queryId);

      if (match) {
        const articleSlug =
          match.slug || slugify(match.title);

        navigate(`/the-log/${articleSlug}`, {
          replace: true
        });
      }
    }
  }, [queryId, posts, navigate]);

  // Update SEO
  useEffect(() => {
    if (activeArticle) {
      updateArticleSEO(activeArticle);
    } else {
      document.title =
        'The Syndicate Journal & Expedition Logs | Open Water Syndicate';
    }
  }, [activeArticle]);

  return (
    <div className="min-h-screen bg-ows-white pt-32 pb-20">
      {!activeArticle && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="mb-24 text-center">
            {/* <div className="flex items-center justify-center gap-3 text-ows-teal mb-6">
              <BookOpen className="h-6 w-6" />
              <span className="text-xs font-bold tracking-[0.5em] uppercase">Expedition Log</span>
            </div> */}
            <h1 className="ows-header mb-8">
              The Expedition Log<span className="text-ows-orange italic font-normal lowercase font-display">&</span> <br />
              Journal
            </h1>
            <p className="max-w-2xl mx-auto text-ows-dark/60 text-lg font-serif italic">
              Dispatches from the edge of the known world. Real-time updates from our current expeditions.
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {posts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                // onClick={() => setSelectedPost(post)}
                onClick={() => {
                  const articleSlug = post.slug || slugify(post.title);
                  navigate(`/the-log/${articleSlug}`);
                }}
                className="group cursor-pointer"
              >
                <div className="aspect-video bg-ows-sand/20 overflow-hidden mb-8 relative">
                  <img
                    src={resolveMediaUrl(post.image) || undefined}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 bg-ows-white/90 backdrop-blur-sm px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-ows-teal">
                    {post.category}
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-ows-dark/40">
                    <span className="flex items-center gap-2"><Calendar className="h-3 w-3" /> {new Date(post.date).toLocaleDateString()}</span>
                    <span className="flex items-center gap-2"><User className="h-3 w-3" /> {post.author}</span>
                  </div>
                  <Link
                    to={`/the-log/${post.slug || slugify(post.title)}`}
                    className="text-2xl font-serif font-bold uppercase tracking-tight text-ows-dark group-hover:text-ows-teal transition-colors"
                  >
                    {post.title}
                  </Link>
                  <p className="text-ows-dark/60 text-sm font-serif italic leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="pt-4 flex items-center gap-2 text-ows-teal text-[10px] font-bold uppercase tracking-widest group-hover:gap-4 transition-all">
                    <Link
                      to={`/the-log/${post.slug || slugify(post.title)}`}
                      className="pt-4 flex items-center gap-2 text-ows-teal text-[10px] font-bold uppercase tracking-widest group-hover:gap-4 transition-all"
                    >
                      Read Dispatch
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      )}

      {/* Post Detail Modal */}
      {/* {activeArticle && (
        <article className="single-article">
          <div className="max-w-5xl mx-auto">

            <Link
              to="/the-log"
              className="inline-flex items-center gap-2 text-ows-teal text-xs font-bold uppercase tracking-widest mb-8"
            >
              ← Back to Journal
            </Link>

            <div className="aspect-[21/9] w-full overflow-hidden">
              <img
                src={resolveMediaUrl(activeArticle.image) || undefined}
                alt={activeArticle.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="max-w-3xl mx-auto px-8 py-20">

              <div className="flex items-center gap-6 text-[10px] font-bold uppercase tracking-widest text-ows-teal mb-8">
                <span>{activeArticle.category}</span>

                <span className="w-8 h-px bg-ows-teal/20" />

                <span>
                  {new Date(activeArticle.date).toLocaleDateString()}
                </span>

                <span className="w-8 h-px bg-ows-teal/20" />

                <span>
                  By {activeArticle.author}
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-serif font-black uppercase tracking-tighter text-ows-dark mb-12 leading-tight">
                {activeArticle.title}
              </h1>

              <div className="prose prose-lg prose-ows max-w-none">
                <ReactMarkdown>
                  {activeArticle.content}
                </ReactMarkdown>
              </div>

            </div>
            <ArticleShare
              title={activeArticle.title}
              slug={activeArticle.slug}
            />
          </div>
        </article>
      )} */}

      {/* Post Detail Page */}
      {activeArticle && (
        <article className="single-article w-full overflow-x-hidden">
          <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 md:px-0">

            {/* Back to Journal */}
            <Link
              to="/the-log"
              className="inline-flex items-center gap-2 text-ows-teal text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-5 sm:mb-8"
            >
              ← Back to Journal
            </Link>

            {/* Hero Image */}
            <div className="aspect-[4/3] sm:aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden rounded-xl sm:rounded-2xl md:rounded-none">
              <img
                src={resolveMediaUrl(activeArticle.image) || undefined}
                alt={activeArticle.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div className="w-full max-w-3xl mx-auto px-1 sm:px-4 md:px-8 py-10 sm:py-14 md:py-20">

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-x-5 md:gap-x-6 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-ows-teal mb-6 sm:mb-8">
                <span>{activeArticle.category}</span>

                <span className="w-4 sm:w-6 md:w-8 h-px bg-ows-teal/20 shrink-0" />

                <span>
                  {new Date(activeArticle.date).toLocaleDateString()}
                </span>

                <span className="w-4 sm:w-6 md:w-8 h-px bg-ows-teal/20 shrink-0" />

                <span>
                  By {activeArticle.author}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-serif font-black uppercase tracking-tighter text-ows-dark dark:text-white mb-8 sm:mb-10 md:mb-12 leading-[1.08] break-words">
                {activeArticle.title}
              </h1>

              {/* Markdown Content */}
              <div className="prose prose-lg prose-ows max-w-none">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-2xl sm:text-3xl md:text-5xl font-display font-light uppercase tracking-[0.05em] md:tracking-[0.1em] mb-8 sm:mb-10 md:mb-12 mt-12 sm:mt-16 md:mt-20 text-ows-dark dark:text-white leading-tight break-words">
                        {children}
                      </h1>
                    ),

                    h2: ({ children }) => (
                      <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-light uppercase tracking-[0.08em] md:tracking-[0.15em] mb-6 sm:mb-8 mt-10 sm:mt-14 md:mt-16 text-ows-dark dark:text-white border-b border-ows-teal/10 pb-3 sm:pb-4 break-words">
                        {children}
                      </h2>
                    ),

                    h3: ({ children }) => (
                      <h3 className="text-lg sm:text-xl md:text-2xl font-display font-bold uppercase tracking-[0.1em] md:tracking-[0.2em] mb-5 sm:mb-6 mt-8 sm:mt-10 md:mt-12 text-ows-dark/80 dark:text-white/80 break-words">
                        {children}
                      </h3>
                    ),

                    p: ({ children }) => (
                      <p className="text-base sm:text-lg md:text-xl font-journal leading-[1.75] md:leading-[1.8] text-ows-dark/80 dark:text-white/80 mb-7 sm:mb-10 tracking-normal break-words">
                        {children}
                      </p>
                    ),

                    blockquote: ({ children }) => (
                      <blockquote className="border-l-2 sm:border-l-4 border-ows-teal pl-4 sm:pl-6 md:pl-8 py-3 sm:py-4 my-8 sm:my-12 bg-ows-teal/5 dark:bg-ows-teal/20 italic text-lg sm:text-xl md:text-2xl font-serif text-ows-teal/90 dark:text-ows-teal rounded-r-2xl sm:rounded-r-3xl overflow-hidden">
                        {children}
                      </blockquote>
                    ),

                    ul: ({ children }) => (
                      <ul className="list-none space-y-3 sm:space-y-4 mb-8 sm:mb-10 pl-0 sm:pl-4">
                        {children}
                      </ul>
                    ),

                    ol: ({ children }) => (
                      <ol className="list-decimal space-y-3 sm:space-y-4 mb-8 sm:mb-10 pl-6 sm:pl-8 font-journal text-base sm:text-lg md:text-xl dark:text-white/80">
                        {children}
                      </ol>
                    ),

                    // li: ({ children }) => (
                    //   <li className="flex items-start gap-3 sm:gap-4">
                    //     <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-ows-teal shrink-0 mt-2.5 sm:mt-3" />

                    //     <span className="text-base sm:text-lg md:text-xl font-journal text-ows-dark/80 dark:text-white/80 leading-relaxed break-words">
                    //       {children}
                    //     </span>
                    //   </li>
                    // ),

                    li: ({ children }) => (
                      <li className="text-lg md:text-xl font-journal text-ows-dark/80 dark:text-white/80 leading-[1.8]">
                        {children}
                      </li>
                    ),

                    pre: ({ children }) => (
                      <pre className="whitespace-pre-wrap break-words font-journal text-lg md:text-xl leading-[1.8] text-ows-dark/80 dark:text-white/80 mb-10">
                        {children}
                      </pre>
                    ),

                    img: ({ ...props }) => {
                      const isVideo =
                        props.src?.toLowerCase().endsWith(".mp4") ||
                        props.src?.toLowerCase().endsWith(".webm") ||
                        props.src?.toLowerCase().endsWith(".mov");

                      if (isVideo) {
                        return (
                          <div className="my-10 sm:my-16 md:my-24 space-y-3 sm:space-y-4">
                            <video
                              src={resolveMediaUrl(props.src || "") || undefined}
                              controls
                              playsInline
                              className="w-full max-h-[70vh] object-contain rounded-xl sm:rounded-[2rem] md:rounded-[3rem] shadow-2xl border border-ows-teal/5 block"
                            />

                            {props.alt && (
                              <p className="text-center text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 dark:text-white/40">
                                {props.alt}
                              </p>
                            )}
                          </div>
                        );
                      }

                      return (
                        <div className="my-10 sm:my-16 md:my-24 space-y-3 sm:space-y-4">
                          <img
                            {...props}
                            src={resolveMediaUrl(props.src || "") || undefined}
                            className="w-full max-h-[70vh] object-contain rounded-xl sm:rounded-[2rem] md:rounded-[3rem] shadow-2xl border border-ows-teal/5 block"
                            referrerPolicy="no-referrer"
                          />

                          {props.alt && (
                            <p className="text-center text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 dark:text-white/40">
                              {props.alt}
                            </p>
                          )}
                        </div>
                      );
                    },
                  }}
                >
                  {activeArticle.content}
                </ReactMarkdown>

                {/* Rich Content Blocks */}
                {(activeArticle as any).contentBlocks &&
                  (activeArticle as any).contentBlocks.length > 0 && (
                    <div className="mt-8 sm:mt-12 space-y-8 sm:space-y-12">

                      {(activeArticle as any).contentBlocks.map((block: any) => (
                        <div
                          key={block.id || Math.random().toString()}
                          className="animate-in fade-in slide-in-from-bottom-4 duration-700"
                        >

                          {/* Text Block */}
                          {block.type === "text" && (
                            <ReactMarkdown
                              components={{
                                p: ({ children }) => (
                                  <p className="text-base sm:text-lg md:text-xl font-journal leading-[1.75] md:leading-[1.8] text-ows-dark/80 dark:text-white/80 mb-7 sm:mb-10 tracking-normal break-words">
                                    {children}
                                  </p>
                                ),

                                h2: ({ children }) => (
                                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-light uppercase tracking-[0.08em] md:tracking-[0.15em] mb-6 sm:mb-8 mt-10 sm:mt-14 md:mt-16 text-ows-dark dark:text-white border-b border-ows-teal/10 pb-3 sm:pb-4 break-words">
                                    {children}
                                  </h2>
                                ),

                                h3: ({ children }) => (
                                  <h3 className="text-lg sm:text-xl md:text-2xl font-display font-bold uppercase tracking-[0.1em] md:tracking-[0.2em] mb-5 sm:mb-6 mt-8 sm:mt-10 md:mt-12 text-ows-dark/80 dark:text-white/80 break-words">
                                    {children}
                                  </h3>
                                ),
                              }}
                            >
                              {block.value}
                            </ReactMarkdown>
                          )}

                          {/* Image Block */}
                          {block.type === "image" && (
                            <div className="space-y-4">
                              <img
                                src={resolveMediaUrl(block.value) || undefined}
                                alt={block.caption || "Expedition Detail"}
                                className="w-full max-h-[70vh] object-contain rounded-xl sm:rounded-[2.5rem] md:rounded-[3.5rem] shadow-2xl border border-ows-teal/5 block"
                                referrerPolicy="no-referrer"
                              />

                              {block.caption && (
                                <p className="text-center text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 dark:text-white/40 italic">
                                  {block.caption}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Video Block */}
                          {block.type === "video" && (
                            <div className="space-y-4">
                              <video
                                src={resolveMediaUrl(block.value) || undefined}
                                controls
                                playsInline
                                className="w-full max-h-[70vh] object-contain rounded-xl sm:rounded-[2.5rem] md:rounded-[3.5rem] shadow-2xl border border-ows-teal/5 block"
                              />

                              {block.caption && (
                                <p className="text-center text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 dark:text-white/40 italic">
                                  {block.caption}
                                </p>
                              )}
                            </div>
                          )}

                        </div>
                      ))}
                    </div>
                  )}
              </div>

              {/* Bottom Actions */}
              <div className="mt-12 sm:mt-16 md:mt-20 pt-8 sm:pt-10 md:pt-12 border-t border-ows-teal/10 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-6 sm:gap-4">
                <div className="flex flex-wrap gap-3 sm:gap-4">

                  {/* Existing Article Share Component */}
                  <ArticleShare
                    title={activeArticle.title}
                    slug={activeArticle.slug}
                  />

                </div>

                <Link
                  to="/the-log"
                  className="text-ows-teal text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center gap-2 group"
                >
                  Back to Log

                  <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>

            </div>



          </div>
        </article>
      )}

    </div>
  );
}